import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	Button,
	Typography,
	CircularProgress,
	TextField,
	Card,
	CardContent,
	Divider,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { ErrorModal, useToken, getMomentTz } from "@dexteel/mesf-core";
import {
	updateAssetAttachmentsComments,
	removeAttachmentLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { AssetAttachment } from "@/pages/asset-details/models/AssetAttachment";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";
import { getExtensionStyles } from "@/pages/asset-details/components/hooks/useGetExtensionStyles";
import { DocumentViewerModal } from "@/shared/components/DocumentViewerModal";
import VisibilityIcon from "@mui/icons-material/Visibility";

const moment = getMomentTz();

const commonCardStyles = {
	backgroundColor: "#f8f9fa",
	borderRadius: "6px",
	border: "1px solid #e9ecef",
	marginBottom: "8px",
	display: "flex",
};

const useStyles = makeStyles((theme) => ({
	propertyCard: {
		padding: "8px 16px",
		height: "60px",
		flexDirection: "column",
		justifyContent: "flex-start",
		...commonCardStyles,
	},
	propertyCardFlexibleHeight: {
		padding: "8px 16px",
		height: "100%",
		boxSizing: "border-box",
		flexDirection: "column",
		justifyContent: "flex-start",
		...commonCardStyles,
	},
	propertyCardFileType: {
		padding: "8px 16px",
		height: "60px",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		...commonCardStyles,
	},
	propertyLabel: {
		fontSize: "10px",
		fontWeight: 600,
		color: "#6c757d",
		textTransform: "uppercase",
		letterSpacing: "0.5px",
		marginBottom: "2px",
	},
	propertyValue: {
		fontSize: "13px",
		color: "#212529",
		lineHeight: "1.3",
		flex: 1,
		display: "flex",
		alignItems: "center",
		overflow: "hidden",
	},
	extensionBadge: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: "55px",
		height: "40px",
		borderRadius: "6px",
		fontSize: "11px",
		fontWeight: "bold",
		color: "#fff",
		marginLeft: "10px",
		flexShrink: 0,
	},
	deleteButton: {
		backgroundColor: "#dc3545",
		color: "white",
		minWidth: "100px",
		"&:hover": {
			backgroundColor: "#c82333",
		},
	},
	modalTitle: {
		fontSize: "24px",
		fontWeight: 600,
		color: "#212529",
		textAlign: "center",
	},
	assetInfoRow: {
		marginBottom: "12px",
	},
	assetNameCard: {
		backgroundColor: "#e3f2fd",
		padding: "8px 16px",
		borderRadius: "6px",
		border: "1px solid #bbdefb",
		height: "100%",
		display: "flex",
		alignItems: "center",
	},
	assetNameText: {
		fontSize: "13px",
		fontWeight: 600,
		color: "#1976d2",
	},
	commentsSection: {
		marginTop: "12px",
		paddingTop: "12px",
		borderTop: "1px solid #e9ecef",
	},
	commentsLabel: {
		fontSize: "13px",
		fontWeight: 600,
		color: "#495057",
		marginBottom: "8px",
	},
	messageTitle: {
		fontSize: 14,
		fontWeight: "bold",
		color: "#0000008A",
	},
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
	imagePreviewContainer: {
		margin: "16px 0",
		padding: "16px",
		backgroundColor: "#f8f9fa",
		borderRadius: "8px",
		border: "1px solid #e9ecef",
	},
	imageWrapper: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "280px",
		backgroundColor: "#ffffff",
		borderRadius: "6px",
		border: "1px solid #dee2e6",
		overflow: "hidden",
	},
}));

const VITE_DOCUMENT_PATH =
	import.meta.env.VITE_DOCUMENT_PATH ||
	"https://mill-cmms.roanokesteel.com/vaultix/";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	rowSelected: AssetAttachment;
	searchAssetAttachments: Function;
}

export const PropertiesModal = ({
	show,
	onHide,
	rowSelected,
	searchAssetAttachments,
}: Props) => {
	const { getUserName } = useToken();
	const classes = useStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [comments, setComments] = useState("");
	const [userInput, setUserInput] = useState("");
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [viewerOpen, setViewerOpen] = useState(false);

	useEffect(() => {
		if (rowSelected && show) {
			setComments(rowSelected.Comments || "");
			setUserInput("");
			setIsSubmitLoading(false);
		}
	}, [show, rowSelected]);

	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (
			comments === "" ||
			comments === null ||
			comments === "null" ||
			comments === "undefined" ||
			comments === undefined
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}

		setComments(newValue);
		setUserInput("");
	};

	const addNewComment = () => {
		if (userInput.trim() !== "") {
			appendComment(userInput);
		}
	};

	const onKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			addNewComment();
		}
	};

	const changeComments = () => {};

	const handleSave = async () => {
		try {
			setIsSubmitLoading(true);
			setError("");

			const targetAssetId = rowSelected.AssetId;

			const response = await updateAssetAttachmentsComments(
				targetAssetId,
				rowSelected.UploadFileId,
				comments,
			);

			if (!response.ok) {
				setError(response.message || "Error updating attachment comments");
				return;
			}

			searchAssetAttachments();
			onHide(true);
		} catch (err) {
			setError("An error occurred while saving");
			console.error("Save error:", err);
		} finally {
			setIsSubmitLoading(false);
		}
	};

	const handleDelete = async () => {
		try {
			setIsSubmitLoading(true);
			setError("");

			const targetAssetId = rowSelected.AssetId;

			const response = await removeAttachmentLink(
				targetAssetId,
				rowSelected.UploadFileId,
			);

			if (!response.ok) {
				setError(response.message || "Error removing attachment link");
				return;
			}

			searchAssetAttachments();
			setDeleteConfirmOpen(false);
			onHide(true);
		} catch (err) {
			setError("An error occurred while deleting");
			console.error("Delete error:", err);
		} finally {
			setIsSubmitLoading(false);
		}
	};

	if (!rowSelected) return null;

	const fileExtension =
		rowSelected.FileName?.split(".").pop()?.toUpperCase() || "";
	const extensionStyles = getExtensionStyles(fileExtension);

	const isRenderableDocument = () => {
		const renderableExtensions = [
			"PNG",
			"JPG",
			"JPEG",
			"PDF",
			"GIF",
			"BMP",
			"SVG",
		];
		return renderableExtensions.includes(fileExtension);
	};

	return (
		<>
			<Dialog
				open={show}
				onClose={() => onHide(false)}
				maxWidth="md"
				fullWidth
				className={classes.root}
				PaperProps={{
					style: {
						borderRadius: "8px",
						width: "100%",
						margin: "16px",
						display: "flex",
						flexDirection: "column",
					},
				}}
			>
				<DialogContent
					style={{
						padding: "20px 24px 0px",
						overflowY: "auto",
						flex: "1 1 auto",
					}}
				>
					<Typography className={classes.modalTitle} gutterBottom>
						{rowSelected.AssetName || "Unknown Asset"}
					</Typography>
					<Divider style={{ marginBottom: 10 }} />

					<Grid2 container spacing={2} className={classes.assetInfoRow}>
						<Grid2 size={{ xs: 8 }}>
							<div className={classes.propertyCard}>
								<Typography className={classes.propertyLabel}>
									FILE NAME
								</Typography>
								<Typography className={classes.propertyValue}>
									{rowSelected.FileName}
								</Typography>
							</div>
						</Grid2>
						<Grid2 size={{ xs: 4 }}>
							<div className={classes.propertyCardFileType}>
								<div>
									<Typography className={classes.propertyLabel}>
										FILE TYPE
									</Typography>
									<Typography className={classes.propertyValue}>
										{fileExtension}
									</Typography>
								</div>
								<div className={classes.extensionBadge} style={extensionStyles}>
									{fileExtension}
								</div>
							</div>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 6 }}>
							<div className={classes.propertyCard}>
								<Typography className={classes.propertyLabel}>
									LINKED BY
								</Typography>
								<Typography className={classes.propertyValue}>
									{rowSelected.LinkedBy}
								</Typography>
							</div>
						</Grid2>
						<Grid2 size={{ xs: 6 }}>
							<div className={classes.propertyCard}>
								<Typography className={classes.propertyLabel}>
									UPLOAD DATE
								</Typography>
								<Typography className={classes.propertyValue}>
									{rowSelected.CreationTime
										? new Date(rowSelected.CreationTime).toLocaleDateString()
										: "N/A"}
								</Typography>
							</div>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={1}>
						<Grid2 size={{ xs: 12 }}>
							<div className={classes.commentsSection}>
								<Typography className={classes.commentsLabel}>
									Comments
								</Typography>
								<Card
									variant="outlined"
									style={{ backgroundColor: "#E9F2F8" }}
									className={classes.root}
								>
									<CardContent style={{ padding: "8px" }}>
										<Typography className={classes.messageTitle}>
											Comments:
										</Typography>
										<SimpleTextAreaControl
											value={comments || ""}
											onChange={changeComments}
											disabled
											showColor={false}
											styleText={{
												height: "70px",
												border: "none",
												backgroundColor: "#E9F2F8",
												fontSize: 15,
												padding: "4px 8px",
											}}
										/>
									</CardContent>
								</Card>
								<Grid2
									container
									spacing={1}
									justifyContent="center"
									alignItems="center"
									style={{ marginTop: "8px" }}
								>
									<Grid2 size={{ md: 9, xs: 12 }}>
										<TextField
											variant="outlined"
											margin="dense"
											multiline
											maxRows={3}
											value={userInput}
											onChange={(e) => setUserInput(e.target.value)}
											label="Comment"
											fullWidth
											onKeyDown={onKeyDown}
											autoComplete="off"
											className={classes.comments}
											size="small"
										/>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={addNewComment}
											style={{ height: "38px", marginTop: 2 }}
										>
											ADD COMMENT
										</Button>
									</Grid2>
								</Grid2>
							</div>
						</Grid2>
					</Grid2>
				</DialogContent>

				<Divider />

				<DialogActions
					style={{
						padding: "16px 24px",
						justifyContent: "space-between",
						alignItems: "center",
						minHeight: "68px",
						height: "68px",
						flexShrink: 0,
					}}
				>
					<div style={{ display: "flex", gap: "8px" }}>
						<Button
							variant="contained"
							className={classes.deleteButton}
							startIcon={<DeleteIcon />}
							onClick={() => setDeleteConfirmOpen(true)}
							disabled={isSubmitLoading}
						>
							DELETE
						</Button>
						{isRenderableDocument() && (
							<Button
								variant="contained"
								color="primary"
								style={{
									minWidth: "100px",
								}}
								startIcon={<VisibilityIcon />}
								onClick={() => setViewerOpen(true)}
								disabled={isSubmitLoading}
							>
								VIEW
							</Button>
						)}
					</div>

					<div style={{ display: "flex", gap: "8px" }}>
						<Button
							variant="contained"
							color="inherit"
							onClick={() => onHide(false)}
							disabled={isSubmitLoading}
							style={{ minWidth: "225px" }}
						>
							CANCEL
						</Button>
						<Button
							startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
							disabled={isSubmitLoading}
							variant="contained"
							color="primary"
							onClick={handleSave}
							style={{ minWidth: "225px" }}
						>
							SAVE
						</Button>
					</div>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
				maxWidth="sm"
			>
				<DialogContent>
					<Typography>
						Are you sure you want to delete this attachment link?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button
						onClick={handleDelete}
						color="secondary"
						disabled={isSubmitLoading}
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						variant="contained"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<DocumentViewerModal
				open={viewerOpen}
				onClose={() => setViewerOpen(false)}
				title={rowSelected?.FileName || "Attachment"}
				documentPath={rowSelected?.Path}
				extension={fileExtension}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
